import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401 — uses raw axios (not the intercepted `api` instance) to prevent loops
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) prom.resolve(token);
    else prom.reject(error);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Never retry auth endpoints
    const url = originalRequest?.url || '';
    if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue requests while a refresh is in-flight
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Use raw axios — completely bypasses interceptors, no recursion possible
        const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
          processQueue(null, data.access_token);
          return api(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Format API error detail — NEVER expose internal/system messages to users
const BLOCKED_KEYWORDS = ['emergent', 'traceback', 'exception', 'pydantic', 'mongodb', 'motor', 'uvicorn', 'starlette', 'fastapi', 'unhandled', 'internal server'];

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';

  let message = '';
  if (typeof detail === 'string') {
    message = detail;
  } else if (Array.isArray(detail)) {
    // Pydantic validation array — extract only msg fields
    message = detail
      .map((e) => {
        if (e && typeof e.msg === 'string') return e.msg;
        return null;
      })
      .filter(Boolean)
      .join(' ');
  } else if (detail && typeof detail.msg === 'string') {
    message = detail.msg;
  } else {
    return 'Something went wrong. Please try again.';
  }

  // Block any internal/system references from reaching the user
  const lower = message.toLowerCase();
  if (BLOCKED_KEYWORDS.some((kw) => lower.includes(kw))) {
    return 'Something went wrong. Please try again.';
  }

  // Block messages that look like stack traces or raw error objects
  if (message.includes('File "') || message.includes('line ') || message.includes('Error:') || message.length > 300) {
    return 'Something went wrong. Please try again.';
  }

  return message || 'Something went wrong. Please try again.';
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await api.post('/auth/register', userData);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors
    }
    localStorage.removeItem('access_token');
    setUser(false);
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const { data } = await api.post('/auth/google/callback', { session_id: sessionId });
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    handleGoogleCallback,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { api, formatApiErrorDetail };
